import toast from "react-hot-toast";
import CustomAlert from "../components/Alerts/CustomAlert";
import {
  fetchAuthSession,
  fetchUserAttributes,
  signOut,
} from "aws-amplify/auth";
import { clearListCache } from "./listCache";

/* =====================================================
   FORMATADORES
===================================================== */

export const formatCurrency = (valor, moeda) => {
  return valor.toLocaleString("pt-br", {
    style: "currency",
    currency: moeda,
  });
};

export const truncateText = (text, number) => {
  if (!text) return "";
  return String(text).length < number
    ? text
    : text.substring(0, number) + "...";
};

/* =====================================================
   TOAST
===================================================== */

export const notify = (type, text, right) => {
  toast.remove();
  toast.custom(<CustomAlert message={text} type={type} right={right} />, {
    position: right ? "top-center" : "bottom-left",
  });
};

/* =====================================================
   AUTH HELPERS (AMPLIFY V6)
===================================================== */

// fetchUserAttributes é uma chamada de rede ao Cognito; como getTokenData
// roda em praticamente todo request de API, os atributos são cacheados por
// alguns minutos e chamadas concorrentes compartilham a mesma promise.
// fetchAuthSession NÃO é cacheado aqui: é resolução local do Amplify e é
// quem garante a renovação do token.
const USER_ATTRIBUTES_TTL_MS = 5 * 60 * 1000;
let _userAttributesCache = null;
let _userAttributesCachedAt = 0;
let _userAttributesInflight = null;

export const getCachedUserAttributes = async () => {
  const now = Date.now();

  if (_userAttributesCache && now - _userAttributesCachedAt < USER_ATTRIBUTES_TTL_MS) {
    return _userAttributesCache;
  }

  if (!_userAttributesInflight) {
    _userAttributesInflight = fetchUserAttributes()
      .then((attributes) => {
        _userAttributesCache = attributes;
        _userAttributesCachedAt = Date.now();
        return attributes;
      })
      .finally(() => {
        _userAttributesInflight = null;
      });
  }

  return _userAttributesInflight;
};

export const clearUserAttributesCache = () => {
  _userAttributesCache = null;
  _userAttributesCachedAt = 0;
};

export const getTokenData = async () => {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });

    const token = session?.tokens?.accessToken?.toString() || null;
    const client_id = session?.tokens?.idToken?.toString() || null;

    let email = null;
    let is_email_verified = false;

    try {
      const attributes = await getCachedUserAttributes();
      email = attributes?.email || null;
      is_email_verified =
        String(attributes?.email_verified).toLowerCase() === "true";
    } catch {
      // Não quebra fluxo se falhar
    }

    return { token, client_id, email, is_email_verified };
  } catch (err) {
    console.error("Erro ao obter sessão:", err);

    return {
      token: null,
      client_id: null,
      email: null,
      is_email_verified: false,
    };
  }
};

export const handleLogout = async () => {
  try {
    clearUserAttributesCache();
    clearListCache();
    await signOut();
    window.location.href = process.env.REACT_APP_FRONTEND_URL;
  } catch (error) {
    console.error("Erro ao fazer logout:", error);
  }
};

/* =====================================================
   MASKS
===================================================== */

export const insertMaskForCpfOrCnpj = (value) => {
  const clean = value.replace(/\D/g, "");

  if (clean.length <= 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  return clean.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5"
  );
};

export const insertMaskForPhoneNumber = (phone) => {
  const clean = phone.replace(/\D/g, "");
  const { length } = clean;

  if (length <= 11) {
    return clean
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(length === 11 ? /(\d{5})(\d)/ : /(\d{4})(\d)/, "$1-$2");
  }

  return phone;
};

export const insertMaskForCep = (text) => {
  return text
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
};

/* =====================================================
   UTILITÁRIOS GERAIS
===================================================== */

export function createObjectFromArray(array, initialValue) {
  return array.reduce((acc, key) => {
    acc[key] = initialValue;
    return acc;
  }, {});
}

export function removeKey(obj, keyToRemove) {
  const { [keyToRemove]: _, ...rest } = obj;
  return rest;
}

export function getLocation(fullLocation) {
  if (!fullLocation) return "";

  const locationName = fullLocation.split("/")[1];

  const map = {
    "cobran%C3%A7as": "/cobranças",
    "configura%C3%A7%C3%B5es": "/configurações",
    "integra%C3%A7%C3%B5es": "/integrações",
  };

  return map[locationName] || "/" + locationName;
}

/* =====================================================
   TEXT VARIABLE HIGHLIGHT
===================================================== */

export const updatedField = (text, variablesOfTemplate) => {
  if (!text || !variablesOfTemplate) return text;

  const variables = Object.values(variablesOfTemplate).flat();

  variables.forEach((variable) => {
    const regex = new RegExp(`{{\\s*${variable}\\s*}}`, "g");
    text = String(text).replace(
      regex,
      `<span style="color:#4C8435;font-weight:600;">{{${variable}}}</span>`
    );
  });

  return text;
};

/* =====================================================
   DATE
===================================================== */

export function formatNotifyDate(dateString) {
  const months = [
    "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
  ];

  const [day, month] = dateString.split("/");
  return `${day} ${months[parseInt(month, 10) - 1]}`;
}

/* =====================================================
   TEMPLATE FORMATTER
===================================================== */

export function updateMessageType(object) {
  const correctedArray = object.messages.map((item) => {
    const content = String(item.message.content);
    const isURL = content.startsWith("https://");
    const isBase64 = content.startsWith("data:image/");
    const isSMS = item.type === "sms";

    if (isURL || isBase64) {
      return { id: item.id, type: "img", message: content, fromMe: item.fromMe };
    }

    if (isSMS) {
      return { id: item.id, type: "sms", message: content, fromMe: item.fromMe };
    }

    return { id: item.id, type: "whatsapp", message: content, fromMe: item.fromMe };
  });

  return { messages: correctedArray, type: "whatsapp" };
}

/* =====================================================
   PLANOS
===================================================== */

export const translatePlan = (plan) => {
  const map = {
    FreeZap: "Gratuito",
    StartZap: "Iniciante",
    ProZap: "Profissional",
    UltraZap: "Empresarial",
  };

  return map[plan] || plan;
};
